import './lab-edit.scss';

import PropTypes from 'prop-types';
import * as R from 'ramda';
import React from 'react';
import Button from 'react-bootstrap/Button';

import BooleanBox from '../../components/boolean-box';
import ButtonInput from '../../components/button-input';
import DatePicker from '../../components/date-picker';
import FieldGroup from '../../components/field-group';
import SingleSelect from '../../components/single-select';
import TextInput from '../../components/text-input';
import Well from '../../components/well';
import { validate } from '../../utils/validation';

import { FIELD_LABELS, FIELD_PLACEHOLDERS, LAB_FIELDS } from './lab-constants';
import { labEditSchema } from './lab-edit-schema';

class LabEdit extends React.Component {
    constructor(props) {
        super(props);
        if (!props.labReducer.productResults.length) {
            props.fetchProducts();
        }

        this.state = R.mergeRight(R.pick(LAB_FIELDS, props.labReducer.currentLab), {
            errors: {},
            submitted: false,
        });

        this.handleFormChanges = R.bind(this.handleFormChanges, this);
        this.handleUpsert = R.bind(this.handleUpsert, this);
        this.QuickInput = R.bind(this.QuickInput, this);
        this.QuickFile = R.bind(this.QuickFile, this);
        this.QuickBool = R.bind(this.QuickBool, this);
        this.handleFiles = R.bind(this.handleFiles, this);
    }

    componentDidUpdate({ labReducer }) {
        const { currentLab } = this.props.labReducer;

        if (JSON.stringify(labReducer.currentLab) !== JSON.stringify(currentLab)) {
            this.setState(R.mergeRight(R.pick(LAB_FIELDS, currentLab), { errors: {}, submitted: false }));
        }
    }

    handleFiles({ target }) {
        const reader = new FileReader();
        const field = target.id;

        reader.onload = () =>
            this.setState({
                [field]: reader.result,
            });
        reader.readAsText(target.files[0]);
    }

    QuickFile(field) {
        return (
            <TextInput
                id={field}
                type="file"
                value={this.state[field]}
                label={FIELD_LABELS[field]}
                onChange={this.handleFiles}
                accept=".json"
            />
        );
    }

    handleFormChanges({ target }) {
        if (target.type === 'checkbox') {
            if (target.id === 'pfSense_format' && target.checked) {
                this.setState({ pfSense_tls: 1 });
            }
            if (target.id === 'ovpn_linux' && target.checked) {
                this.setState({ ovpn_linux_resolved: 1 });
            }
            target = R.assoc('value', target.checked ? 1 : 0, target);
        } else if (target.id === 'type' && target.value !== 'VPN') {
            //unset Exceptions
            this.setState({
                tls_cipher_exception_linux: 0,
                tls_cipher_exception_windows: 0,
            });
        }
        this.setState({ [target.id]: target.value }, () => {
            this.setState((state) =>
                state.submitted ? { errors: validate(labEditSchema, R.pick(LAB_FIELDS, state)).errors } : null,
            );
        });
    }

    handleUpsert() {
        const values = R.pick(LAB_FIELDS, this.state);
        const { valid, errors } = validate(labEditSchema, values);

        this.setState({ errors, submitted: true });

        if (valid) {
            this.props.upsertLab(values);
        }
    }

    QuickInput(field, type) {
        return (
            <TextInput
                id={field}
                value={this.state[field]}
                type={type}
                label={FIELD_LABELS[field]}
                placeholder={FIELD_PLACEHOLDERS[field]}
                error={this.state.errors[field]}
                onChange={this.handleFormChanges}
            />
        );
    }

    QuickBool(field) {
        return (
            <BooleanBox
                id={field}
                field={field}
                label={FIELD_LABELS[field]}
                checked={!!this.state[field]}
                onChange={this.handleFormChanges}
            />
        );
    }
    render() {
        const {
            props: { labReducer },
            QuickInput,
            QuickBool,
            QuickFile,
            state,
        } = this;
        const blueprintFile = this.state.blueprint_file;

        return (
            <Well className="lab-edit">
                <h2>Add/Edit Labs here</h2>
                <div className="mb-3">
                    <label>ID:</label> {this.state.id || 'an ID will be generated upon save'}
                </div>
                {QuickInput('name')}
                <SingleSelect
                    id="status"
                    label={FIELD_LABELS.status}
                    error={state.errors.status}
                    selected={state.status}
                    options={[
                        {
                            label: 'Disabled',
                            value: 'disabled',
                        },
                        {
                            label: 'Deleted',
                            value: 'deleted',
                        },
                        {
                            label: 'Legacy',
                            value: 'legacy',
                        },
                        {
                            label: 'Current',
                            value: 'current',
                        },
                        {
                            label: 'Development',
                            value: 'development',
                        },
                    ]}
                    onChange={this.handleFormChanges}
                />
                <SingleSelect
                    id="product_id"
                    label={FIELD_LABELS.product_id}
                    error={state.errors.product_id}
                    selected={state.product_id}
                    options={R.map(
                        (product) => ({ value: product.id, label: product.name }),
                        labReducer.productResults,
                    )}
                    onChange={this.handleFormChanges}
                />
                <SingleSelect
                    id="type"
                    label={FIELD_LABELS.type}
                    error={state.errors.type}
                    selected={state.type}
                    options={[
                        {
                            label: 'VPN',
                            value: 'VPN',
                        },
                        {
                            label: 'LODS',
                            value: 'LODS',
                        },
                        {
                            label: 'LODS_CLOUD',
                            value: 'LODS_CLOUD',
                        },
                        {
                            label: 'HTTP_AUTH',
                            value: 'HTTP_AUTH',
                        },
                        {
                            label: 'NETWARS',
                            value: 'NETWARS',
                        },
                        {
                            label: 'PYWARS',
                            value: 'PYWARS',
                        },
                    ]}
                    onChange={this.handleFormChanges}
                />
                <SingleSelect
                    id="section"
                    label={FIELD_LABELS.section}
                    selected={state.section}
                    options={[
                        {
                            label: '1',
                            value: '1',
                        },
                        {
                            label: '6',
                            value: '6',
                        },
                    ]}
                    onChange={this.handleFormChanges}
                />
                {QuickInput('on_demand_training_id_start' /*, 'number' //@TODO */)}
                {QuickInput('on_demand_training_id_end' /*, 'number' //@TODO */)}
                {QuickInput('courseware_start')}
                {QuickInput('courseware_end')}
                <DatePicker
                    id="live_event_start"
                    label={FIELD_LABELS.live_event_start}
                    value={state.live_event_start}
                    error={state.errors.live_event_start}
                    onChange={this.handleFormChanges}
                />
                <DatePicker
                    id="live_event_end"
                    label={FIELD_LABELS.live_event_end}
                    value={state.live_event_end}
                    error={state.errors.live_event_end}
                    onChange={this.handleFormChanges}
                />
                {this.state.type === 'VPN' && (
                    <FieldGroup className="exceptions" title="OVPN Exceptions">
                        {QuickBool('tls_cipher_exception_linux')}
                        {QuickBool('tls_cipher_exception_windows')}
                    </FieldGroup>
                )}
                <FieldGroup className="includes" title="Event Type Includes">
                    {QuickBool('include_ondemand')}
                    {QuickBool('include_vlive')}
                    {QuickBool('include_simulcast')}
                    {QuickBool('include_mentor')}
                    {QuickBool('include_community')}
                    {QuickBool('include_conference')}
                    {QuickBool('include_mpo')}
                </FieldGroup>
                {state.type === 'NETWARS' && (
                    <FieldGroup className="netwars-fields" title={state.type}>
                        {QuickInput('game_name')}
                        {QuickInput('host_name')}
                        {QuickInput('auth_key')}
                        {QuickInput('s3_bucket')}
                        {R.isNil(blueprintFile) || R.isEmpty(blueprintFile) ? (
                            QuickFile('blueprint_file')
                        ) : (
                            <ButtonInput
                                id="blueprint_file"
                                label={FIELD_LABELS.blueprint_file}
                                onClick={this.handleFormChanges}
                                value=""
                                text="Remove File"
                            ></ButtonInput>
                        )}
                        <DatePicker
                            id="course_start_date"
                            label={FIELD_LABELS.course_start_date}
                            value={state.course_start_date}
                            onChange={this.handleFormChanges}
                        />
                        {QuickBool('auto_start')}
                    </FieldGroup>
                )}
                {state.type === 'VPN' && (
                    <FieldGroup className="vpn-fields" title={state.type}>
                        {this.state.include_simulcast === 1 && QuickInput('parent' /*, 'number' //@TODO */)}
                        {QuickBool('sim_lab')}
                        {QuickInput('default_fqdn')}
                        {QuickBool('pfSense_format')}
                        {state.pfSense_format === 1 && <div className="ms-4">{QuickBool('pfSense_tls')}</div>}
                        {QuickBool('ovpn_linux')}
                        {state.ovpn_linux === 1 && <div className="ms-4">{QuickBool('ovpn_linux_resolved')}</div>}
                        {QuickBool('ovpn_windows')}
                        {QuickInput('ip_address')}
                        {QuickInput('gateway_ip')}
                        {QuickInput('gateway_name')}
                    </FieldGroup>
                )}
                {state.type === 'LODS_CLOUD' && (
                    <FieldGroup className="lods-cloud-fields" title={state.type}>
                        {QuickBool('shared_environment')}
                        {QuickInput('lab_series')}
                    </FieldGroup>
                )}
                <Button variant="success" onClick={this.handleUpsert}>
                    {R.pathOr(false, ['currentLab', 'id'], labReducer) ? 'Update' : 'Add'} Lab
                </Button>
            </Well>
        );
    }
}

LabEdit.propTypes = {
    labReducer: PropTypes.object,
    fetchProducts: PropTypes.func,
    upsertLab: PropTypes.func,
    match: PropTypes.object,
};

export default LabEdit;
